// services/liability.js — ограничена имуществена отговорност на работника
// (чл. 206 и 210 КТ) и допустимата месечна удръжка (чл. 446 ГПК).
//
// Нормативна основа (сверена в актуалната редакция на 25.08.2026):
// - чл. 206 КТ: отговорност в размер на вредата, но не повече от
//   УГОВОРЕНОТО месечно трудово възнаграждение; при отчетническа дейност —
//   до ТРИКРАТНИЯ му размер.
// - чл. 210 КТ: заповед в 1-месечен срок от ОТКРИВАНЕТО на вредата, но не
//   по-късно от 1 година от ПРИЧИНЯВАНЕТО ѝ; оспорване в 1-месечен срок
//   спира удръжките (съдебен ред); удръжката се прави от нетото и не може
//   да надвишава размера по чл. 446 ГПК.
// - чл. 446, ал. 1 ГПК (МРЗ-кратни, актуална скала): до 1 МРЗ —
//   несеквестируемо; 1–2 МРЗ → 1/3 (без деца) / 1/4 (с деца); 2–4 МРЗ →
//   1/2 / 1/3; над 4 МРЗ → горницата над 2 МРЗ / над 2,5 МРЗ.
//   Ал. 3 (ДВ 55/2025): дробите се смятат върху пълния доход и на лицето
//   ВИНАГИ остава поне 1 МРЗ.

const { round2 } = require('./totals');

// МРЗ по дата на валидност — конфигурационна таблица, не заровени числа.
// Най-новият ред е пръв; minWageAt() взима първия с from <= датата.
const MIN_WAGE_TABLE = [
  { from: '2026-01-01', eur: 620.2, bgn: 1213, source: 'ПМС № 243/13.11.2025 (от 01.01.2026 и официално в евро)' },
  { from: '2025-01-01', eur: 550.66, bgn: 1077, source: '1077 лв. от 01.01.2025 (≈550,66 € по фиксирания курс)' },
];

function minWageAt(dateIso) {
  const d = String(dateIso || '');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d)) throw new Error('Невалидна дата: ' + dateIso);
  const row = MIN_WAGE_TABLE.find((r) => r.from <= d);
  if (!row) throw new Error('Няма въведена МРЗ за ' + d + ' — допълнете MIN_WAGE_TABLE в services/liability.js.');
  return row.eur;
}

// Таванът по чл. 206 КТ: вредата, но не повече от 1 (или 3 — отчетник) БТВ.
function liabilityLimit(opts) {
  const o = opts || {};
  const damage = Number(o.damage);
  const gross = Number(o.monthlyGross);
  if (!(damage >= 0)) throw new Error('Въведете размер на вредата.');
  if (!(gross > 0)) throw new Error('Въведете уговореното месечно брутно възнаграждение.');
  const cap = round2(gross * (o.accountable ? 3 : 1));
  const amount = round2(Math.min(damage, cap));
  return {
    cap, amount,
    capped: damage > cap,
    notes: [
      o.accountable
        ? 'Отчетническа дейност: таванът е ТРИКРАТНИЯТ размер на уговореното месечно възнаграждение (чл. 206, ал. 2 КТ).'
        : 'Таванът е уговореното месечно трудово възнаграждение (чл. 206, ал. 1 КТ). При отчетническа дейност — трикратният размер.',
      'При умисъл или вреда от престъпление отговорността е пълна, по гражданския ред — този калкулатор не важи.',
    ],
  };
}

// Допустимата МЕСЕЧНА удръжка от нетото по чл. 446 ГПК. Скалата е върху
// МРЗ към датата; ал. 3 гарантира, че на лицето остава поне 1 МРЗ.
function garnishablePart(opts) {
  const o = opts || {};
  const net = Number(o.netIncome);
  if (!(net >= 0)) throw new Error('Въведете нетен месечен доход.');
  const mw = minWageAt(o.date || new Date().toISOString().slice(0, 10));
  const withKids = !!o.hasChildren;
  const notes = [];

  let amount;
  if (net <= mw) {
    amount = 0;
    notes.push('Доходът е до 1 МРЗ (' + mw.toFixed(2).replace('.', ',') + ' €) — изцяло несеквестируем (чл. 446, ал. 1 ГПК).');
  } else if (net <= 2 * mw) {
    amount = net * (withKids ? 1 / 4 : 1 / 3);
    notes.push('Доход между 1 и 2 МРЗ → ' + (withKids ? '1/4 (с деца)' : '1/3 (без деца)') + ' от пълния доход.');
  } else if (net <= 4 * mw) {
    amount = net * (withKids ? 1 / 3 : 1 / 2);
    notes.push('Доход между 2 и 4 МРЗ → ' + (withKids ? '1/3 (с деца)' : '1/2 (без деца)') + ' от пълния доход.');
  } else {
    amount = net - (withKids ? 2.5 : 2) * mw;
    notes.push('Доход над 4 МРЗ → горницата над ' + (withKids ? '2,5' : '2') + ' МРЗ.');
  }

  // Ал. 3 (ДВ 55/2025): на лицето винаги остава поне 1 МРЗ.
  if (net - amount < mw) {
    amount = Math.max(0, net - mw);
    notes.push('Коригирано по ал. 3 (ДВ 55/2025): на лицето винаги остава поне 1 МРЗ.');
  }

  return { amount: round2(amount), minWage: mw, notes };
}

// Срокът за заповедта по чл. 210, ал. 2 КТ: по-ранното от 1 месец след
// откриването и 1 година след причиняването.
function orderDeadline(opts) {
  const o = opts || {};
  const addMonths = (iso, months) => {
    const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) throw new Error('Невалидна дата: ' + iso);
    const last = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + months + 1, 0)).getUTCDate();
    const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1 + months, Math.min(Number(m[3]), last)));
    const p = (x) => (x < 10 ? '0' : '') + x;
    return d.getUTCFullYear() + '-' + p(d.getUTCMonth() + 1) + '-' + p(d.getUTCDate());
  };
  const byDiscovery = addMonths(o.discovered, 1);
  const byCause = addMonths(o.caused, 12);
  return {
    deadline: byDiscovery < byCause ? byDiscovery : byCause,
    byDiscovery, byCause,
    notes: ['Оспорване от работника в 1-месечен срок СПИРА удръжките — вредата се търси по съдебен ред (чл. 210, ал. 3 КТ).'],
  };
}

module.exports = { liabilityLimit, garnishablePart, orderDeadline, minWageAt, MIN_WAGE_TABLE };
